// ══════════════════════════════════════════════════════
// PeptideVault v6.5 — Unified Protocol Engine
// Single source of truth for all protocol-derived views.
// Every consumer calls this module rather than reimplementing schedule math.
// ══════════════════════════════════════════════════════

// ── Timezone-safe date helpers ──
function parseLD(s) { var p = s.split('-'); return new Date(+p[0], +p[1]-1, +p[2]) }
function fmtLD(d) { return d.getFullYear() + '-' + String(d.getMonth()+1).padStart(2,'0') + '-' + String(d.getDate()).padStart(2,'0') }
function addDays(d, n) { var r = new Date(d); r.setDate(r.getDate() + n); return r }
function daysBetween(a, b) { return Math.round((parseLD(b) - parseLD(a)) / 86400000) }

// ── Frequency labels ──
var FREQ_MAP = { daily:'Daily', eod:'Every Other Day', '3x-week':'3×/Week', weekly:'Weekly', biweekly:'Biweekly', custom:'Custom' }
function freqLabel(f) { return FREQ_MAP[f] || f }

// ── Unit conversion ──
function doseToMg(amount, unit) {
  var n = parseFloat(amount)
  if (!n || isNaN(n)) return null
  if (unit === 'mg') return n
  if (unit === 'mcg') return n / 1000
  return null  // IU/units: no conversion without concentration
}

function canConvertToMg(unit) { return unit === 'mg' || unit === 'mcg' }

function normalizePlanningUnit(unit) {
  var value = String(unit || '').trim().toLowerCase().replace(/\s+/g, '')
  if (value === 'mcg' || value === 'μg' || value === 'ug') return 'mcg'
  if (value === 'mg') return 'mg'
  if (value === 'iu' || value === 'i.u.' || value === 'internationalunits') return 'IU'
  return String(unit || '').trim()
}

function planningDose(amount, unit) {
  var value = Number(amount)
  var normalized = normalizePlanningUnit(unit)
  if (!isFinite(value) || value <= 0 || !normalized) return null
  if (normalized === 'mcg') return { amount:value / 1000, unit:'mg' }
  if (normalized === 'mg') return { amount:value, unit:'mg' }
  if (normalized === 'IU') return { amount:value, unit:'IU' }
  return null
}

function planningDoseForEvent(event, fallbackUnit) {
  var components = event && event.components
  if (Array.isArray(components) && components.length) {
    var total = 0
    var unit = ''
    for (var i = 0; i < components.length; i++) {
      var component = planningDose(components[i].numericValue ?? components[i].doseAmount, components[i].unit || fallbackUnit)
      if (!component || (unit && component.unit !== unit)) return null
      unit = component.unit
      total += component.amount
    }
    if (total > 0 && unit) return { amount:+total.toFixed(unit === 'IU' ? 2 : 4), unit:unit }
  }
  return planningDose(event && event.doseAmount, event && (event.doseUnit || fallbackUnit))
}

function skuVialStrength(sku) {
  if (!sku) return null
  var value = Number(sku.vialStrength)
  var unit = normalizePlanningUnit(sku.vialStrengthUnit)
  if (isFinite(value) && value > 0 && ['mcg','mg','IU'].indexOf(unit) >= 0) return { value:value, unit:unit }
  // Backward compatibility: all pre-7.8.36 strength fields were explicitly mg.
  value = Number(sku.vialStrengthMg || sku.mgPerVial)
  if (isFinite(value) && value > 0) return { value:value, unit:'mg' }
  return null
}

function skuStrengthForUnit(sku, requestedUnit) {
  var strength = skuVialStrength(sku)
  var target = normalizePlanningUnit(requestedUnit)
  if (!strength || !target) return null
  if (strength.unit === target) return strength.value
  if (strength.unit === 'mcg' && target === 'mg') return strength.value / 1000
  if (strength.unit === 'mg' && target === 'mcg') return strength.value * 1000
  return null
}

function formatVialStrength(sku) {
  var strength = skuVialStrength(sku)
  return strength ? strength.value + ' ' + strength.unit : ''
}

// ══════════════════════════════════════════════════════
// CORE: Resolve a protocol into an array of PlannedEvents
// PlannedEvent = { date, protocolId, protocolName, peptideId, peptideName,
//                  doseAmount, doseUnit, doseMg, phaseName, isBreak:false }
// ══════════════════════════════════════════════════════

function resolveProtocolSchedule(protocol) {
  var p = protocol
  if (!p || !p.startDate) return []

  // P1-4: Explicit schedule mode — use stored dated entries directly
  if (p.scheduleMode === 'explicit' && p.explicitEntries && p.explicitEntries.length > 0) {
    return p.explicitEntries.map(function(e) {
      var planned = planningDoseForEvent(e, p.doseUnit)
      var mg = planned && planned.unit === 'mg' ? planned.amount : null
      return {
        date: e.date,
        time: e.time || e.timeLabel || '',
        timeLabel: e.timeLabel || '',
        administrationIndex: e.administrationIndex == null ? null : e.administrationIndex,
        protocolId: p.id,
        protocolName: p.name || p.peptideName || '',
        peptideId: p.peptideId || '',
        peptideName: p.peptideName || '',
        doseAmount: e.doseAmount,
        doseUnit: e.doseUnit || 'mcg',
        doseMg: mg,
        planningDose: planned,
        phaseName: '',
        status: p.status || 'upcoming',
        rawValue: e.rawValue || '',
        components: e.components || null
      }
    })
  }

  var start = parseLD(p.startDate)
  var end = p.endDate ? parseLD(p.endDate) : addDays(start, 90)
  var freq = p.frequency || 'daily'
  var weekdays = p.weekdays || []
  var breaks = p.breaks || []
  var phases = p.phases || []
  var startDow = start.getDay()
  var events = []
  var dayCount = 0

  for (var d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    var key = fmtLD(d)
    var dow = d.getDay()

    // Check breaks
    var inBreak = false
    for (var bi = 0; bi < breaks.length; bi++) {
      if (breaks[bi].startDate && breaks[bi].endDate && key >= breaks[bi].startDate && key <= breaks[bi].endDate) {
        inBreak = true; break
      }
    }
    if (inBreak) { dayCount++; continue }

    // Check frequency
    var scheduled = false
    if (freq === 'daily') scheduled = true
    else if (freq === 'eod') scheduled = (dayCount % 2 === 0)
    else if (freq === '3x-week') scheduled = ([1,3,5].indexOf(dow) >= 0)
    else if (freq === 'weekly') scheduled = (dow === startDow)
    else if (freq === 'biweekly') scheduled = (dow === startDow && dayCount % 14 < 7)
    else if (freq === 'custom') scheduled = (weekdays.indexOf(dow) >= 0)

    if (scheduled) {
      // Determine active phase for this day
      var dayNum = dayCount
      var activePhase = null
      for (var pi = 0; pi < phases.length; pi++) {
        if (dayNum >= (phases[pi].startDay || 0) && dayNum <= (phases[pi].endDay || 9999)) {
          activePhase = phases[pi]; break
        }
      }

      var amt = activePhase ? (activePhase.doseAmount || p.doseAmount) : p.doseAmount
      var unit = activePhase ? (activePhase.doseUnit || p.doseUnit) : p.doseUnit
      var mg = doseToMg(amt, unit)

      events.push({
        date: key,
        protocolId: p.id,
        protocolName: p.name || p.peptideName || '',
        peptideId: p.peptideId || '',
        peptideName: p.peptideName || '',
        doseAmount: amt,
        doseUnit: unit || 'mcg',
        doseMg: mg,
        planningDose: planningDose(amt, unit),
        phaseName: activePhase ? (activePhase.name || '') : '',
        status: p.status || 'upcoming'
      })
    }
    dayCount++
  }
  return events
}

// ── Resolve ALL active protocols ──
function _cloneProtocolForResolution(p) {
  try { return JSON.parse(JSON.stringify(p)) } catch (e) { return Object.assign({}, p) }
}

function _protocolBounds(p) {
  var dates = []
  if (p.scheduleMode === 'explicit') {
    ;(p.explicitEntries || []).forEach(function(e) { if (e.date) dates.push(e.date) })
    ;(p.components || []).forEach(function(c) { (c.explicitEntries || []).forEach(function(e) { if (e.date) dates.push(e.date) }) })
  }
  dates.sort()
  return { start: dates[0] || p.startDate || '', end: dates[dates.length-1] || p.endDate || p.startDate || '' }
}

function _shiftDateKey(dateKey, deltaDays) {
  if (!dateKey || !deltaDays) return dateKey
  return fmtLD(addDays(parseLD(dateKey), deltaDays))
}

function _shiftProtocolDerived(p, newStart) {
  if (!p || !p.startDate || !newStart || p.startDate === newStart) return p
  var oldStart = p.startDate
  var delta = daysBetween(oldStart, newStart)
  p.startDate = newStart
  if (p.endDate) p.endDate = _shiftDateKey(p.endDate, delta)
  if (p.scheduleMode === 'explicit') {
    p.explicitEntries = (p.explicitEntries || []).map(function(e) { var x = Object.assign({}, e); x.date = _shiftDateKey(e.date, delta); return x })
    p.components = (p.components || []).map(function(c) {
      var cc = Object.assign({}, c)
      cc.explicitEntries = (c.explicitEntries || []).map(function(e) { var x = Object.assign({}, e); x.date = _shiftDateKey(e.date, delta); return x })
      return cc
    })
  }
  return p
}

function _applyPauseDuringDerived(p, pauseStart, pauseEnd) {
  if (!p || !pauseStart || !pauseEnd) return p
  if (p.scheduleMode === 'explicit') {
    var keep = function(e) { return !e.date || e.date < pauseStart || e.date > pauseEnd }
    p.explicitEntries = (p.explicitEntries || []).filter(keep)
    p.components = (p.components || []).map(function(c) { var cc = Object.assign({}, c); cc.explicitEntries = (c.explicitEntries || []).filter(keep); return cc })
  } else {
    p.breaks = (p.breaks || []).slice()
    p.breaks.push({ startDate: pauseStart, endDate: pauseEnd, reason: 'Protocol dependency: pause-during' })
  }
  return p
}

// ── Resolve ALL active protocols ──
// v6.5: dependency resolution is PURE. Stored protocol objects are never mutated.
function resolveAllProtocols(protocols) {
  var all = []
  var resolved = (protocols || []).map(_cloneProtocolForResolution)
  var deps = (typeof S !== 'undefined' && S.protocolDependencies) || []

  if (deps.length > 0) {
    var byId = {}
    resolved.forEach(function(p) { byId[p.id] = p })
    for (var pass = 0; pass < 8; pass++) {
      var changed = false
      deps.forEach(function(dep) {
        var target = byId[dep.protocolId]
        if (!target) return
        var related = byId[dep.relatedProtocolId]
        if (!related && dep.relatedCompound) {
          var n = dep.relatedCompound.toLowerCase()
          related = resolved.find(function(p) { return String(p.peptideName || p.name || '').toLowerCase() === n })
        }
        if (!related) return
        var rb = _protocolBounds(related)
        if (!rb.end) return
        var offset = Number(dep.offsetDays || 0) || 0

        if (dep.relationType === 'start-after' || dep.relationType === 'relative-offset' || dep.relationType === 'resume-when') {
          var extra = dep.relationType === 'relative-offset' ? offset : offset + 1
          var newStart = fmtLD(addDays(parseLD(rb.end), extra))
          if (target.startDate !== newStart) {
            _shiftProtocolDerived(target, newStart)
            changed = true
          }
        } else if (dep.relationType === 'pause-during') {
          var beforeCount = target.scheduleMode === 'explicit' ?
            (target.explicitEntries || []).length + (target.components || []).reduce(function(n,c){return n+(c.explicitEntries||[]).length},0) :
            (target.breaks || []).length
          _applyPauseDuringDerived(target, rb.start, rb.end)
          var afterCount = target.scheduleMode === 'explicit' ?
            (target.explicitEntries || []).length + (target.components || []).reduce(function(n,c){return n+(c.explicitEntries||[]).length},0) :
            (target.breaks || []).length
          if (afterCount !== beforeCount) changed = true
        }
      })
      if (!changed) break
    }
  }

  for (var i = 0; i < resolved.length; i++) {
    var p = resolved[i]
    if (p.isSupplementSchedule) continue
    if (p.status === 'active' || p.status === 'upcoming') {
      if (p.isMultiComponent && p.components && p.components.length > 0) {
        for (var ci = 0; ci < p.components.length; ci++) {
          var comp = p.components[ci]
          var subProto = { id: p.id, name: p.name, peptideId: comp.peptideId || p.peptideId, peptideName: comp.compoundName, status: p.status, startDate: p.startDate, endDate: p.endDate, frequency: p.frequency, doseAmount: comp.doseAmount, doseUnit: comp.doseUnit, scheduleMode: 'explicit', explicitEntries: comp.explicitEntries || [], phases: p.phases, breaks: p.breaks }
          all = all.concat(resolveProtocolSchedule(subProto))
        }
      } else {
        all = all.concat(resolveProtocolSchedule(p))
      }
    }
  }
  return all
}

// ── Filter helpers ──
function eventsForDate(events, dateStr) {
  return events.filter(function(e) { return e.date === dateStr })
}

function eventsInRange(events, startDate, endDate) {
  return events.filter(function(e) { return e.date >= startDate && e.date <= endDate })
}

function eventsForPeptide(events, peptideId) {
  return events.filter(function(e) { return e.peptideId === peptideId })
}

// ══════════════════════════════════════════════════════
// INVENTORY FORECASTING
// ══════════════════════════════════════════════════════

// v6.5: inventory helpers match the production backup schema.
function skuOnHandQty(sku) {
  if (!sku) return 0
  var locs = sku.locations || []
  if (Array.isArray(locs)) return locs.reduce(function(sum, loc) { return sum + Math.max(0, Number((loc || {}).qty) || 0) }, 0)
  if (typeof locs === 'object') return Object.keys(locs).reduce(function(sum, key) { return sum + Math.max(0, Number(locs[key]) || 0) }, 0)
  return 0
}

function skuMgPerVial(sku) {
  // Legacy API retained for mg-based consumers. Never reinterpret IU as mg.
  return skuStrengthForUnit(sku, 'mg')
}


function forecastInventory(protocols, peptides, reservations, windowDays) {
  var today = fmtLD(new Date())
  var endDate = fmtLD(addDays(new Date(), windowDays || 90))

  // Resolve all planned events in the window
  var allEvents = resolveAllProtocols(protocols)
  var windowEvents = eventsInRange(allEvents, today, endDate)

  // Group by peptide
  var byPeptide = {}
  for (var i = 0; i < windowEvents.length; i++) {
    var ev = windowEvents[i]
    var pid = ev.peptideId
    if (!pid) continue
    if (!byPeptide[pid]) byPeptide[pid] = { peptideId: pid, peptideName: ev.peptideName, events: [], totalAmount: 0, amountUnit:'', totalDoses: 0, canCalculate: true }
    byPeptide[pid].events.push(ev)
    byPeptide[pid].totalDoses++
    var plannedDose = ev.planningDose || planningDoseForEvent(ev, ev.doseUnit)
    if (!plannedDose) byPeptide[pid].canCalculate = false
    else if (byPeptide[pid].amountUnit && byPeptide[pid].amountUnit !== plannedDose.unit) byPeptide[pid].canCalculate = false
    else { byPeptide[pid].amountUnit = plannedDose.unit; byPeptide[pid].totalAmount += plannedDose.amount }
  }

  // Build forecast results
  var results = []
  for (var pid in byPeptide) {
    var entry = byPeptide[pid]
    var peptide = null
    for (var j = 0; j < (peptides || []).length; j++) {
      if (peptides[j].id === pid || peptides[j].name === entry.peptideName) { peptide = peptides[j]; break }
    }

    // On hand (vials)
    var onHand = 0
    var skuStrengths = []
    var earliestExpiry = null
    var expiringBeforeEnd = 0
    if (peptide) {
      for (var k = 0; k < (peptide.skus || []).length; k++) {
        var sku = peptide.skus[k]
        var qty = skuOnHandQty(sku)
        if (qty <= 0) continue
        onHand += qty
        var amountPerVial = skuStrengthForUnit(sku, entry.amountUnit)
        if (amountPerVial) skuStrengths.push({ mg: amountPerVial, amount:amountPerVial, unit:entry.amountUnit, qty: qty, expiry: sku.expiryDate || null, skuId: sku.id || '' })
        if (sku.expiryDate) {
          if (!earliestExpiry || sku.expiryDate < earliestExpiry) earliestExpiry = sku.expiryDate
          if (sku.expiryDate <= endDate) expiringBeforeEnd += qty
        }
      }
    }

    // Reserved
    var reserved = 0
    for (var ri = 0; ri < (reservations || []).length; ri++) {
      if (reservations[ri].peptideId === pid) reserved += Math.max(0, Number(reservations[ri].qty != null ? reservations[ri].qty : reservations[ri].quantity) || 0)
    }

    var available = Math.max(0, onHand - reserved)

    // P0-4+P0-5: True chronological per-event FEFO with multi-vial consumption and reservation exclusion
    var vialsNeeded = 0
    var vialsCalculable = entry.canCalculate && skuStrengths.length > 0
    var allocationExplanation = { eligibleVials:0, reservedVials:reserved, expiredExcluded:0, fefo:[], unmetMg:0, unmetAmount:0, amountUnit:entry.amountUnit }
    if (vialsCalculable) {
      // Build inventory pool: individual vials with expiry and remaining mg
      var vialPool = []
      skuStrengths.forEach(function(sku) {
        for (var v = 0; v < sku.qty; v++) {
          vialPool.push({ mg: sku.mg, remainingMg: sku.mg, expiry: sku.expiry || '9999-12-31', state: 'available', usedMg: 0, skuId: sku.skuId || '' })
        }
      })

      // P1-4: Honor SKU-specific reservations — match by skuId first, then fallback
      vialPool.sort(function(a, b) { return a.expiry.localeCompare(b.expiry) })
      var pepReservations = (reservations || []).filter(function(r) { return r.peptideId === entry.peptideId })
      pepReservations.forEach(function(res) {
        var qty = Math.max(0, Number(res.qty != null ? res.qty : res.quantity) || 0)
        if (res.skuId) {
          // Reserve the specific SKU
          for (var rsi = 0; rsi < vialPool.length && qty > 0; rsi++) {
            if (vialPool[rsi].skuId === res.skuId && vialPool[rsi].state === 'available') { vialPool[rsi].state = 'reserved'; qty-- }
          }
        }
        // Fallback: reserve from latest-expiring available
        for (var rfi = vialPool.length - 1; rfi >= 0 && qty > 0; rfi--) {
          if (vialPool[rfi].state === 'available') { vialPool[rfi].state = 'reserved'; qty-- }
        }
      })
      var usablePool = vialPool.filter(function(v) { return v.state === 'available' })

      // Sort events chronologically
      var sortedEvents = entry.events.slice().sort(function(a, b) { return a.date.localeCompare(b.date) })

      var expiredUnused = 0
      var unmetMg = 0

      for (var ei = 0; ei < sortedEvents.length; ei++) {
        var ev = sortedEvents[ei]
        var eventDose = ev.planningDose || planningDoseForEvent(ev, ev.doseUnit)
        var remainingNeed = eventDose && eventDose.unit === entry.amountUnit ? eventDose.amount : 0
        if (remainingNeed <= 0) continue

        // P0-4: Multi-vial consumption loop — dose can span multiple vials
        while (remainingNeed > 0.001) {
          // Find earliest-expiring vial with remaining mg that hasn't expired
          var foundVial = false
          for (var vi = 0; vi < usablePool.length; vi++) {
            var vial = usablePool[vi]
            if (vial.state === 'expired' || vial.remainingMg <= 0) continue
            if (vial.expiry < ev.date) {
              // Expired before this event
              if (vial.state === 'available') { expiredUnused++; vial.state = 'expired' }
              continue
            }
            // Consume from this vial
            var consume = Math.min(vial.remainingMg, remainingNeed)
            vial.remainingMg -= consume
            vial.usedMg += consume
            if (vial.state === 'available') vial.state = 'used'
            remainingNeed -= consume
            foundVial = true
            break // re-enter while loop to check if more vials needed
          }
          if (!foundVial) {
            unmetMg += remainingNeed
            break // no more usable vials
          }
        }
      }

      // Mark any remaining unconsumed vials that expire within window
      for (var ve = 0; ve < usablePool.length; ve++) {
        if (usablePool[ve].state === 'available' && usablePool[ve].expiry <= endDate) {
          expiredUnused++
          usablePool[ve].state = 'expired'
        }
      }

      // Count: only vials from which mg was actually consumed
      var usedVials = usablePool.filter(function(v) { return v.state === 'used' }).length
      allocationExplanation.eligibleVials = usablePool.length
      allocationExplanation.expiredExcluded = expiredUnused
      allocationExplanation.unmetMg = +unmetMg.toFixed(3)
      allocationExplanation.unmetAmount = +unmetMg.toFixed(3)
      allocationExplanation.fefo = usablePool.filter(function(v){return v.usedMg>0}).map(function(v){return { skuId:v.skuId, expiry:v.expiry==='9999-12-31'?null:v.expiry, strengthMg:v.mg, usedMg:+v.usedMg.toFixed(3), remainingMg:+v.remainingMg.toFixed(3) }})
      // Fix 9: Purchase-strength-aware — find preferred purchase strength
      var purchaseStrengths = skuStrengths.map(function(s) { return s.mg }).filter(function(v,i,a) { return a.indexOf(v) === i }).sort(function(a,b) { return b-a })
      var primaryStrength = purchaseStrengths[0] || 1
      var additionalVialsNeeded = unmetMg > 0 ? Math.ceil(unmetMg / primaryStrength) : 0
      vialsNeeded = usedVials + additionalVialsNeeded
      expiringBeforeEnd = expiredUnused
      // Store purchase alternatives for UI
      var purchaseAlternatives = purchaseStrengths.map(function(mg) { return { mg: mg, vialsNeeded: unmetMg > 0 ? Math.ceil(unmetMg / mg) : 0 } })
    }

    // P0-2: Shortfall = additional vials needed for unmet demand (not raw available comparison)
    var shortfall = vialsCalculable ? additionalVialsNeeded : null

    results.push({
      peptideId: pid,
      peptideName: entry.peptideName,
      totalDoses: entry.totalDoses,
      totalMg: entry.canCalculate && entry.amountUnit === 'mg' ? +entry.totalAmount.toFixed(2) : null,
      canCalcMg: entry.canCalculate && entry.amountUnit === 'mg',
      totalAmount: entry.canCalculate ? +entry.totalAmount.toFixed(entry.amountUnit === 'IU' ? 2 : 4) : null,
      amountUnit: entry.amountUnit,
      canCalculate: entry.canCalculate,
      onHand: onHand,
      reserved: reserved,
      available: available,
      vialsNeeded: vialsCalculable ? vialsNeeded : null,
      shortfall: shortfall,
      earliestExpiry: earliestExpiry,
      expiringBeforeEnd: expiringBeforeEnd,
      skuStrengths: skuStrengths,
      purchaseStrength: vialsCalculable && typeof primaryStrength !== 'undefined' ? primaryStrength : null,
      purchaseAlternatives: vialsCalculable && typeof purchaseAlternatives !== 'undefined' ? purchaseAlternatives : [],
      windowDays: windowDays || 90,
      events: entry.events
      ,allocationExplanation: allocationExplanation
    })
  }

  results.sort(function(a, b) {
    if (a.shortfall !== null && b.shortfall !== null) return b.shortfall - a.shortfall
    if (a.shortfall !== null) return -1
    if (b.shortfall !== null) return 1
    return a.peptideName.localeCompare(b.peptideName)
  })

  return results
}

// ── Purchase planning (derived from forecast) ──
function purchasePlan(forecastResults) {
  return forecastResults.filter(function(r) {
    return r.shortfall > 0
  }).map(function(r) {
    // P1-7: shortfall already accounts for FEFO unusable expiring vials, so suggestedQty = shortfall
    var suggestedQty = r.shortfall || 0
    return {
      peptideName: r.peptideName,
      peptideId: r.peptideId,
      shortfall: r.shortfall || 0,
      expiringBeforeEnd: r.expiringBeforeEnd,
      suggestedQty: suggestedQty,
      totalMg: r.totalMg,
      windowDays: r.windowDays
    }
  })
}

export {
  parseLD,
  fmtLD,
  addDays,
  daysBetween,
  FREQ_MAP,
  freqLabel,
  doseToMg,
  canConvertToMg,
  normalizePlanningUnit,
  planningDose,
  planningDoseForEvent,
  resolveProtocolSchedule,
  resolveAllProtocols,
  eventsForDate,
  eventsInRange,
  eventsForPeptide,
  forecastInventory,
  purchasePlan,
  skuOnHandQty,
  skuMgPerVial,
  skuVialStrength,
  skuStrengthForUnit,
  formatVialStrength
}
